import { SimpleAction, PayloadAction } from 'interfaces/action'
import { createAction, createPayloadAction } from 'helpers/actionsHelpers'
import {
  RawPeergroupRelation,
  PeergroupsTotalsCollection,
  PeergroupFilterName,
  PeergroupFormType,
  Peergroup,
  RelationId,
  PeergroupCollection,
  PeergroupRelationCollection,
} from 'interfaces/peergroups'

// COMMON ACTIONS
export const GET_PEER_GROUPS = 'GET_PEER_GROUPS' as const
type GetPeergroups = SimpleAction<typeof GET_PEER_GROUPS>
export const getPeergroupsActionCreator = () =>
  createAction(GET_PEER_GROUPS)

type SetPeergroupsPayload = {
  peergroups: PeergroupCollection,
  relations: PeergroupRelationCollection,
}
export const SET_PEER_GROUPS = 'SET_PEER_GROUPS' as const
type SetPeergroups = PayloadAction<typeof SET_PEER_GROUPS, SetPeergroupsPayload>
export const setPeergroupsActionCreator = (payload: SetPeergroupsPayload) =>
  createPayloadAction(SET_PEER_GROUPS, payload)

export const GET_PEER_GROUP_TOTALS = 'GET_PEER_GROUP_TOTALS' as const
type GetPeergroupsTotals = PayloadAction<typeof GET_PEER_GROUP_TOTALS, { date: string, ids: number[] }>
export const getPeergroupsTotalsActionCreator = (date: string, ids: number[]): GetPeergroupsTotals =>
  createPayloadAction(GET_PEER_GROUP_TOTALS, { date, ids })

export const APPEND_PEER_GROUPS_TOTALS = 'APPEND_PEER_GROUPS_TOTALS' as const
type AppendPeergroupsTotals = PayloadAction<typeof APPEND_PEER_GROUPS_TOTALS, { date: string, totals: PeergroupsTotalsCollection }>
export const appendPeergroupsTotalsActionCreator = (date: string, totals: PeergroupsTotalsCollection) =>
  createPayloadAction(APPEND_PEER_GROUPS_TOTALS, { date, totals })

export const RANDOMIZE_PEER_GROUP_RELATION = 'RANDOMIZE_PEER_GROUP_RELATION' as const
type RandomizePeergroup = SimpleAction<typeof RANDOMIZE_PEER_GROUP_RELATION>
export const randomizePeergroupRelationActionCreator = () =>
  createAction(RANDOMIZE_PEER_GROUP_RELATION)

export const SET_RANDOM_RELATION = 'SET_RANDOM_RELATION' as const
type SetRandomRelation = PayloadAction<typeof SET_RANDOM_RELATION, RawPeergroupRelation>
export const setRandomRelationActionCreator = (payload: RawPeergroupRelation) =>
  createPayloadAction(SET_RANDOM_RELATION, payload)

export const TOGGLE_SIDEBAR_PEER_GROUPS = 'TOGGLE_SIDEBAR_PEER_GROUPS' as const
type ToggleSidebarPeergroups = PayloadAction<typeof TOGGLE_SIDEBAR_PEER_GROUPS, boolean>
export const toggleSidebarPeergroupsActionCreator = (payload: boolean) =>
  createPayloadAction(TOGGLE_SIDEBAR_PEER_GROUPS, payload)


// ===========================================================================//
//                                 RELATIONS                                  //
// ===========================================================================//

export const APPEND_PEER_GROUP_RELATION = 'APPEND_PEER_GROUP_RELATION' as const
type AppendPeergroupRelation = PayloadAction<typeof APPEND_PEER_GROUP_RELATION, RawPeergroupRelation>
export const appendPeergroupRelationActionCreator = (peergroupRelation: RawPeergroupRelation): AppendPeergroupRelation =>
  createPayloadAction(APPEND_PEER_GROUP_RELATION, peergroupRelation)

export const UPDATE_PEER_GROUP_RELATION = 'UPDATE_PEER_GROUP_RELATION' as const
type SetPeergroupById = PayloadAction<typeof UPDATE_PEER_GROUP_RELATION, RawPeergroupRelation>
export const updatePeergroupRelationActionCreator = (payload: RawPeergroupRelation) =>
  createPayloadAction(UPDATE_PEER_GROUP_RELATION, payload)

export const REQUEST_DELETE_RELATION = 'REQUEST_DELETE_RELATION' as const
type RequestDeleteRelation = PayloadAction<typeof REQUEST_DELETE_RELATION, RelationId>
export const requestDeleteRelationActionCreator = (payload: RelationId) =>
  createPayloadAction(REQUEST_DELETE_RELATION, payload)

export const DELETE_RELATION = 'DELETE_RELATION' as const
type DeleteRelation = PayloadAction<typeof DELETE_RELATION, RelationId>
export const deleteRelationActionCreator = (payload: RelationId) =>
  createPayloadAction(DELETE_RELATION, payload)

export const SELECT_RELATION = 'SELECT_RELATION' as const
type SelectRelation = PayloadAction<typeof SELECT_RELATION, RelationId>
export const selectRelationActionCreator = (relationId: RelationId) =>
  createPayloadAction(SELECT_RELATION, relationId)

// ===========================================================================//
//                                FORM ACTIONS                                //
// ===========================================================================//

export const FIND_PEER_GROUP = 'FIND_PEER_GROUP' as const
type FindPeergroup = PayloadAction<typeof FIND_PEER_GROUP, {[name: string]: string}>
export const findPeergroupActionCreator = (payload: {[name: string]: string}) =>
  createPayloadAction(FIND_PEER_GROUP, payload)

export const SET_FOUND_PEER_GROUP = 'SET_FOUND_PEER_GROUP' as const
type SetPeergroup = PayloadAction<typeof SET_FOUND_PEER_GROUP, Peergroup>
export const setFoundPeergroupActionCreator = (payload: Peergroup) =>
  createPayloadAction(SET_FOUND_PEER_GROUP, payload)

export const SET_PEERGROUP_FILTERS_POPUP_SHOWN = 'SET_PEERGROUP_FILTERS_POPUP_SHOWN' as const
type SetPeergroupFiltersPopupShown = PayloadAction<typeof SET_PEERGROUP_FILTERS_POPUP_SHOWN, boolean>
export const setPeergroupFiltersPopupShown = (isVisible: boolean): SetPeergroupFiltersPopupShown =>
  createPayloadAction(SET_PEERGROUP_FILTERS_POPUP_SHOWN, isVisible)

export const RESET_PEERGROUP_FILTERS = 'RESET_PEERGROUP_FILTERS' as const
type ResetPeergroupFilters = SimpleAction<typeof RESET_PEERGROUP_FILTERS>
export const resetPeergroupFiltersActionCreator =
  () => createAction(RESET_PEERGROUP_FILTERS)

export const SET_PEER_GROUP_FILTERS = 'SET_PEER_GROUP_FILTERS' as const
type SetPeergroupFilters = PayloadAction<typeof SET_PEER_GROUP_FILTERS, PeergroupFilterName[]>
export const setPeergroupFiltersActionCreator = (payload: PeergroupFilterName[]) =>
  createPayloadAction(SET_PEER_GROUP_FILTERS, payload)

export const ADD_PEER_GROUP_FILTER = 'ADD_PEER_GROUP_FILTER' as const
type AddPeergroupFilter = PayloadAction<typeof ADD_PEER_GROUP_FILTER, PeergroupFilterName>
export const addPeergroupFilterActionCreator = (payload: PeergroupFilterName) =>
  createPayloadAction(ADD_PEER_GROUP_FILTER, payload)

export const DELETE_PEER_GROUP_FILTER = 'DELETE_PEER_GROUP_FILTER' as const
type DeletePeergroupFilter = PayloadAction<typeof DELETE_PEER_GROUP_FILTER, { form: PeergroupFormType, filter: PeergroupFilterName }>
export const deletePeergroupFilterActionCreator = (form: PeergroupFormType, filter: PeergroupFilterName) =>
  createPayloadAction(DELETE_PEER_GROUP_FILTER, { form, filter })

export type PeerGroupActions =
  | GetPeergroups
  | AppendPeergroupRelation
  | SetPeergroups
  | GetPeergroupsTotals
  | AppendPeergroupsTotals
  | SetPeergroupById
  | RandomizePeergroup
  | SetRandomRelation
  | RequestDeleteRelation
  | DeleteRelation
  | SelectRelation
  | FindPeergroup
  | SetPeergroup
  | SetPeergroupFiltersPopupShown
  | ResetPeergroupFilters
  | SetPeergroupFilters
  | AddPeergroupFilter
  | DeletePeergroupFilter
  | ToggleSidebarPeergroups

export default PeerGroupActions
